import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {DataTableActionType} from 'notebook/data-tables/DataTableReduxActions';
import {BenchlingAction} from 'redux-stores/Action';

import {AssaySchema} from './assay-schemas/AssaySchemaConstants';
import {AssayRecordActionType} from './assays/AssayRecordActions';
import {AssayResult, AssayRun, DATA_MISSING} from './assays/AssayRecordConstants';
import {FilteredAssayResultsActionType} from './assays/FilteredAssayResultsActions';
import {Filters} from './assays/FilteredAssayResultsActions';
import {ServerResultsTableSpec} from './assays/ResultsTableSpecConstants';

export interface AssayResultsById {
  [resultId: string]: AssayResult | DataMissing;
}
interface AssayRunsById {
  [runId: string]: AssayRun | DataMissing;
}
export interface AssayResultIdsByRunId {
  [runId: string]: Array<string> | DataMissing;
}

interface FilteredAssayResultResponse {
  filters: Filters;
  serverResultsTableSpecs: Array<ServerResultsTableSpec>;
}

export type DataMissing = typeof DATA_MISSING;

export interface ResultsState {
  assayResultsById: AssayResultsById;
  assayResultIdsByRunId: AssayResultIdsByRunId;
  assayRunsById: AssayRunsById;
  assayResultSchemasByRunId: {[runId: string]: Array<AssaySchema>};
  runIdToResultSchemaToCount: {[runId: string]: {[resultSchemaId: string]: number}};
  assayRunsWithResultsFetching: {[runId: string]: boolean};
  // Ideally stored as serverResultsTableSpecsByFilters keyed by filters, but filters is an object so cannot
  // be easily used as a key. Callers can just use the getServerResultsTableSpecs selector, which isolates
  // "filtered AssayResult response" to this module.
  filteredAssayResultResponses: Array<FilteredAssayResultResponse>;
}

export const RESULTS_INITIAL_STATE: ResultsState = {
  assayResultsById: {},
  assayResultIdsByRunId: {},
  assayRunsById: {},
  assayResultSchemasByRunId: {},
  assayRunsWithResultsFetching: {},
  filteredAssayResultResponses: [],
  runIdToResultSchemaToCount: {},
};

function mergeResultsForRun(
  state: ResultsState,
  assayResults: Array<AssayResult>,
  assayRunId: string
): ResultsState {
  return reactUpdate(state, {
    assayResultsById: {$merge: _.indexBy(assayResults, 'id')},
    assayResultIdsByRunId: {
      [assayRunId]: {
        // eslint-disable-next-line no-restricted-syntax
        $set: _.pluck(assayResults, 'id'),
      },
    },
  });
}

function areFiltersMatching(filters1: Filters, filters2: Filters): boolean {
  return isEqual(filters1, filters2);
}

function mergeFilteredAssayResultResponse(
  prevResponses: Array<FilteredAssayResultResponse>,
  filters: Filters,
  serverResultsTableSpecs: Array<ServerResultsTableSpec>
): Array<FilteredAssayResultResponse> {
  const newResponse = {filters, serverResultsTableSpecs};
  const responses = [...prevResponses];
  const index = prevResponses.findIndex((response) => areFiltersMatching(response.filters, filters));
  if (index === -1) {
    responses.push(newResponse);
  } else {
    responses[index] = newResponse;
  }
  return responses;
}

export default function reduceResults(
  state: ResultsState = RESULTS_INITIAL_STATE,
  action: BenchlingAction
): ResultsState {
  switch (action.type) {
    case AssayRecordActionType.ASSAY_RUN_SEARCH_COMPLETED: {
      const {assayRuns, assayResultSchemasByRunId} = action;
      return reactUpdate(state, {
        assayRunsById: {$merge: _.indexBy(assayRuns, 'id')},
        assayResultSchemasByRunId: {$merge: assayResultSchemasByRunId},
      });
    }

    case AssayRecordActionType.ASSAY_RESULT_SEARCH_COMPLETED: {
      const {assayResults} = action;
      return reactUpdate(state, {
        assayResultsById: {$merge: _.indexBy(assayResults, 'id')},
      });
    }

    case AssayRecordActionType.ASSAY_DATA_ATTACHMENTS_SERVER_BOOTSTRAP: {
      const {assayRunsById, assayResultsById, assayResultIdsByRunId} = action;
      return reactUpdate(state, {
        assayRunsById: {$merge: assayRunsById},
        assayResultsById: {$merge: assayResultsById},
        assayResultIdsByRunId: {$merge: assayResultIdsByRunId},
      });
    }

    case AssayRecordActionType.ASSAY_RUNS_FETCH_COMPLETED: {
      const {assayRunsById} = action;
      return reactUpdate(state, {
        assayRunsById: {$merge: assayRunsById},
      });
    }

    case AssayRecordActionType.ASSAY_RUN_UPDATE_COMPLETED: {
      const {assayRun} = action;
      return reactUpdate(state, {
        assayRunsById: {[assayRun.id]: {$set: assayRun}},
      });
    }

    case AssayRecordActionType.ASSAY_RUN_FETCH_DATA_MISSING: {
      return reactUpdate(state, {
        assayRunsById: {
          [action.assayRunId]: {$set: DATA_MISSING},
        },
      });
    }

    case AssayRecordActionType.ASSAY_RESULTS_FETCH_COMPLETED: {
      const {assayResultsById} = action;
      return reactUpdate(state, {
        assayResultsById: {$merge: assayResultsById},
      });
    }

    case AssayRecordActionType.ASSAY_RESULTS_FETCH_DATA_MISSING: {
      return reactUpdate(state, {
        assayResultsById: {
          [action.assayResultId]: {$set: DATA_MISSING},
        },
      });
    }

    case AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_STARTED: {
      const {assayRunId} = action;
      return reactUpdate(state, {
        assayRunsWithResultsFetching: {
          [assayRunId]: {$set: true},
        },
      });
    }

    case AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_PROGRESSED: {
      const {assayResults, assayRunId} = action;
      return mergeResultsForRun(state, assayResults, assayRunId);
    }

    case AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_COMPLETED: {
      const {assayResults, assayRunId} = action;
      return reactUpdate(mergeResultsForRun(state, assayResults, assayRunId), {
        assayRunsWithResultsFetching: {$unset: [assayRunId]},
      });
    }

    case FilteredAssayResultsActionType.FILTERED_ASSAY_RESULTS_FETCH_COMPLETED: {
      const {assayResults, filters, serverResultsTableSpecs} = action;
      return reactUpdate(state, {
        assayResultsById: {
          $merge: _.indexBy(assayResults, 'id'),
        },
        filteredAssayResultResponses: {
          $set: mergeFilteredAssayResultResponse(
            state.filteredAssayResultResponses,
            filters,
            serverResultsTableSpecs
          ),
        },
      });
    }

    case AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_DATA_MISSING: {
      return reactUpdate(state, {
        assayResultIdsByRunId: {
          [action.assayRunId]: {$set: DATA_MISSING},
        },
      });
    }

    case AssayRecordActionType.RUN_RESULTS_COUNTED_BY_SCHEMA: {
      const {assayRunId, resultCountBySchemaId} = action;
      return reactUpdate(state, {
        runIdToResultSchemaToCount: {[assayRunId]: {$set: resultCountBySchemaId}},
      });
    }

    case DataTableActionType.DATA_TABLES_VALIDATION_STATUS_FETCH_COMPLETED: {
      const {assayResultsById} = action;
      return reactUpdate(state, {
        assayResultsById: {$merge: assayResultsById},
      });
    }

    default:
      return state;
  }
}

export function getServerResultsTableSpecs(
  state: ResultsState,
  filters: Filters
): Array<ServerResultsTableSpec> | null {
  const matchingResponse = state.filteredAssayResultResponses.find((response) =>
    areFiltersMatching(response.filters, filters)
  );
  return matchingResponse ? matchingResponse.serverResultsTableSpecs : null;
}

export function filterOutDataMissing<T>(items: Array<T | DataMissing>): Array<T> {
  return items.filter((item) => item !== DATA_MISSING) as Array<T>;
}
